import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import SEO from '../components/SEO';
import { PageContainer } from '../components/PageHeader';
import { pollSubscriptionStatus } from '../features/subscription/services/subscriptionApi';
import { getPendingShareAccept } from '../features/courseShare/pendingShare';

// If PendingShareResumer (app root) hasn't navigated us away from this page
// within this window, its accept call failed without a retryable reason to
// re-queue (see resumePendingShare.js) — assume it's not coming and give the
// user a way out instead of leaving them on a permanent spinner.
const SHARE_RESUME_TIMEOUT_MS = 15000;

export default function SubscriptionSuccessPage() {
    const navigate = useNavigate();
    const { user, ready, subscription, setSubscription } = useAuth();
    const [isPolling, setIsPolling] = useState(true);
    const [pollError, setPollError] = useState(false);
    const [resumingShare, setResumingShare] = useState(false);
    const [shareResumeFailed, setShareResumeFailed] = useState(false);
    const [countdown, setCountdown] = useState(5);

    // Poll for subscription activation.
    // Gated on `ready`: we read the queued share token below, and reading it
    // with an unresolved (null) uid would fail the owner check and send a user
    // who just paid to the dashboard instead of into their new course.
    useEffect(() => {
        if (!ready) return undefined;
        let cancelled = false;

        const activateSubscription = async () => {
            try {
                const status = await pollSubscriptionStatus(
                    (s) => s.active === true,
                    { maxAttempts: 10, intervalMs: 1500 }
                );
                if (cancelled) return;
                setSubscription(status);
                // If the user arrived here mid "Add to my courses" flow, the
                // course build is owned by PendingShareResumer (app root): now
                // that the subscription is active it fires the queued accept and
                // navigates into the new course. Show a builder message and skip
                // the dashboard countdown; leave isPolling true so nothing here
                // navigates away first.
                if (getPendingShareAccept({ uid: user?.uid || null })) {
                    setResumingShare(true);
                    return;
                }
                setIsPolling(false);
            } catch (err) {
                console.error('[SubscriptionSuccess] Polling timeout:', err);
                if (!cancelled) {
                    setPollError(true);
                    setIsPolling(false);
                }
            }
        };

        activateSubscription();

        return () => { cancelled = true; };
    }, [setSubscription, ready, user?.uid]);

    // Escape hatch for the resumingShare state: if the resumer's accept call
    // fails without a retryable reason, it never navigates us away and we'd
    // otherwise be stuck here showing "Adding your course…" forever.
    useEffect(() => {
        if (!resumingShare) return undefined;
        const timer = setTimeout(() => setShareResumeFailed(true), SHARE_RESUME_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [resumingShare]);

    // Start countdown only after polling succeeds
    useEffect(() => {
        if (isPolling || pollError) return;

        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    navigate('/dashboard');
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [navigate, isPolling, pollError]);

    return (
        <>
        <SEO
            title="Subscription Activated"
            description="Your Graspd subscription is now active."
            path="/subscription/success"
            noindex={true}
        />
        <PageContainer style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            textAlign: 'center',
            padding: 'var(--space-7) var(--space-5)'
        }}>
            {/* Icon - changes based on state */}
            <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: (pollError || shareResumeFailed) ? 'var(--warning-bg)' : 'var(--accent-light)',
                border: `1px solid ${(pollError || shareResumeFailed) ? 'var(--warning)' : 'var(--accent-primary)'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 'var(--space-6)'
            }}>
                {isPolling && !shareResumeFailed ? (
                    // Spinner
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--accent-primary)"
                        strokeWidth="2"
                        style={{ animation: 'graspd-spin 1s linear infinite' }}
                    >
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                ) : pollError || shareResumeFailed ? (
                    // Warning icon
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--warning)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                ) : (
                    // Checkmark
                    <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="var(--accent-primary)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                )}
            </div>

            {/* Title */}
            <h1 style={{
                fontSize: '40px',
                fontWeight: '800',
                letterSpacing: '-0.02em',
                marginBottom: 'var(--space-4)',
                color: 'var(--text-primary)'
            }}>
                {shareResumeFailed ? 'We hit a snag' : resumingShare ? 'Adding your course…' : isPolling ? 'Activating...' : pollError ? 'Almost There' : `Welcome to ${subscription?.accountType || 'Pro'}`}
            </h1>

            {/* Description */}
            <p style={{
                fontSize: '18px',
                color: 'var(--text-secondary)',
                marginBottom: 'var(--space-3)',
                maxWidth: '400px',
                lineHeight: '1.6'
            }}>
                {shareResumeFailed
                    ? "We couldn't finish adding your shared course automatically. Your subscription is active — head to your dashboard, or open the share link again to retry."
                    : resumingShare
                        ? 'Your subscription is active — building the shared course on your account now.'
                        : isPolling
                            ? 'Setting up your subscription...'
                            : pollError
                                ? 'Your payment was successful. Please refresh the page in a moment to see your updated subscription.'
                                : 'Your subscription is now active. You have full access to all features.'}
            </p>

            {/* Countdown or status message */}
            <p style={{
                fontSize: '14px',
                color: 'var(--text-tertiary)',
                marginBottom: 'var(--space-6)'
            }}>
                {shareResumeFailed
                    ? ''
                    : isPolling
                        ? 'This usually takes a few seconds...'
                        : pollError
                            ? ''
                            : `Redirecting to dashboard in ${countdown}...`}
            </p>

            {/* Button */}
            <button
                onClick={() => navigate('/dashboard')}
                style={{
                    padding: 'var(--space-3) var(--space-5)',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--btn-primary-bg)',
                    color: 'var(--btn-primary-text)',
                    fontSize: '15px',
                    fontWeight: '600',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                }}
            >
                Go to Dashboard
            </button>
        </PageContainer>
        </>
    );
}